// Cloudinary "fetch" URL wrapper. Routes any external image through
// Cloudinary's CDN for automatic caching, smart-cropping, format negotiation
// (AVIF/WebP), and on-the-fly resizing.
//
// Format: https://res.cloudinary.com/{cloud}/image/fetch/{transformations}/{ENCODED_ORIGINAL_URL}
//
// Why? Unsplash/Pexels are slow on first hit and serve heavy JPEGs. Cloudinary
// caches them edge-locally + delivers in modern formats — typically 30-70%
// smaller and 2-5× faster on mobile.
//
// SAFETY:
//   • Opt-IN via REACT_APP_USE_CLOUDINARY="1". When disabled, returns raw URL
//     unchanged — guarantees images render even if Cloudinary quota is exhausted
//     or the fetch source domain isn't whitelisted in the Cloudinary console.
//   • Skips relative URLs (e.g. /brand/logo.png) — those are served from /public.
//   • Skips base64 data: URIs — those are already inline.
//   • Skips URLs that are already Cloudinary'd.

const CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD;
const ENABLED = process.env.REACT_APP_USE_CLOUDINARY === "1";

const PRESETS = {
  // Above-the-fold hero / featured carousel (homepage What's New, banners).
  hero: "c_fill,w_900,q_auto,f_auto",
  // Standard category/business card thumbnail (mobile-first sizing).
  card: "c_fill,w_400,q_auto,f_auto",
  // Modal / detail view (full-screen biz photo).
  detail: "c_fill,w_900,q_auto,f_auto",
  // Mini thumbnail (e.g. inside dropdowns, popups).
  thumb: "c_fill,w_200,q_auto,f_auto",
};

export function cdnUrl(src, preset = "card") {
  if (!ENABLED || !CLOUD) return src;
  if (!src || typeof src !== "string") return src;
  if (src.startsWith("data:")) return src;
  // Relative asset (served by the public host) — let the browser handle it.
  if (!src.startsWith("http")) return src;
  // Already routed through Cloudinary
  if (src.includes("res.cloudinary.com")) return src;
  const transform = PRESETS[preset] || PRESETS.card;
  // Cloudinary's fetch expects the original URL as a path segment, NOT
  // percent-encoded — it tolerates the raw URL as the suffix.
  return `https://res.cloudinary.com/${CLOUD}/image/fetch/${transform}/${src}`;
}
